/**
 * the merged document's audit log.
 */
import { AUDIT_APPLICATION } from '@helpers/emitTdoAuditEvent';
import { fetchGraphQLApi } from '@helpers/callApi';
import type { TDOId } from '@common-modules/universal/models/Brands';
import {
  SourcedAuditEventRecord,
  formatMergedAuditEventsCsv,
} from '@common/utils/auditLogCsv';

import { withoutLicenseMeterRecords } from '../documents/licenseMeter';

/** The asset type the merged log is stored as, on the project TDO. */
export const MERGED_AUDIT_LOG_ASSET_TYPE = 'redact-audit-log';

/** One TDO to gather events from, and the name its rows should carry. */
export interface MergedAuditLogSource {
  readonly tdoId: string;
  readonly name: string;
}

interface AuditEventsResponse {
  auditEvents?: {
    count?: number;
    records?: Array<{ payload?: Record<string, unknown> }>;
  } | null;
}

const AUDIT_PAGE_SIZE = 100;

const AUDIT_EVENTS_QUERY = `
  query auditEvents($id: ID!, $application: String, $offset: Int, $limit: Int) {
    auditEvents(application: $application, terms: [{ tdoId: $id }], offset: $offset, limit: $limit) {
      count
      records {
        id
        payload
        application
      }
    }
  }
`;

/**
 * Every event for one TDO, paginated to the end.
 */
async function fetchEventsForSource(
  source: MergedAuditLogSource,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{
  readonly records: SourcedAuditEventRecord[];
  /**
   * Did the whole history come back? A read that stops on page 7 of 40 has a
   * plausible-looking prefix and no way to tell, which is the one thing a
   * chain-of-custody document must never be.
   */
  readonly complete: boolean;
}> {
  const gathered: SourcedAuditEventRecord[] = [];
  let offset = 0;
  try {
    for (;;) {
      const result = await fetchGraphQLApi<AuditEventsResponse>({
        endpoint: `${apiRoot}/${graphQLEndpoint}`,
        query: AUDIT_EVENTS_QUERY,
        variables: {
          id: source.tdoId,
          application: AUDIT_APPLICATION,
          offset,
          limit: AUDIT_PAGE_SIZE,
        },
        token,
        veritoneAppId,
      });
      /**
       * AN ERRORED PAGE IS NOT AN EMPTY ONE.
       */
      if (result?.errors?.length || (result?.status && result.status >= 400)) {
        console.warn(
          `[mergedAuditLog] events for ${source.tdoId} came back with errors:`,
          result?.errors ?? result?.status
        );
        return { records: gathered, complete: false };
      }
      const records = result?.data?.auditEvents?.records ?? [];
      for (const record of records) {
        gathered.push({
          payload: record.payload as SourcedAuditEventRecord['payload'],
          sourceName: source.name,
          sourceTdoId: source.tdoId,
        });
      }
      if (records.length < AUDIT_PAGE_SIZE) {
        break;
      }
      offset += AUDIT_PAGE_SIZE;
    }
  } catch (error) {
    console.warn(
      `[mergedAuditLog] events for ${source.tdoId} could not be read:`,
      error
    );
    return { records: gathered, complete: false };
  }
  return { records: gathered, complete: true };
}

/**
 * Does the gathered set already hold this row?
 *
 * Keyed on `(timestamp, action)` — the pair a locally-authored row pins
 * deliberately. Not on the event id, because the caller does not have one:
 * the row it hands in was never read back.
 */
function alreadyGathered(
  gathered: readonly SourcedAuditEventRecord[],
  extra: SourcedAuditEventRecord
): boolean {
  return gathered.some(
    (record) =>
      record.payload?.timestamp === extra.payload?.timestamp &&
      record.payload?.action === extra.payload?.action
  );
}

/**
 * Gather every source's events and format them as one CSV.
 *
 * `extraRecords` are rows the CALLER authored and is not waiting to read back.
 * It exists for one row: the release's own "Built merged document" (VE-31977).
 * That event is emitted to the platform like any other, but `auditEvents` is
 * an Elasticsearch-backed projection — its `query` argument is documented as
 * "an elastic query for audit events" — so it is refresh-delayed, and a write
 * the server has acknowledged is not yet a row the next query returns. An
 * earlier cut awaited the mutation and claimed that settled it; it does not,
 * it only proves the write was accepted. The per-file path that this was
 * modelled on gets away with the same shape because it puts an entire burn-in
 * between its emit and its read; the combine has one round trip and nothing
 * else.
 *
 * The consequence of getting this wrong is silent and bad: the one row saying
 * THIS release was built, at this version, from these documents is missing
 * from the log shipped with it, and the next build's log carries the previous
 * build's row, reading as current. Handing the row over directly is the only
 * version of this that is deterministic.
 *
 * De-duplicated on `(timestamp, action)` so a platform that IS fast enough
 * does not produce the row twice.
 */
export async function buildMergedAuditLogCsv(
  sources: readonly MergedAuditLogSource[],
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string,
  extraRecords: readonly SourcedAuditEventRecord[] = []
): Promise<string | null> {
  if (!sources.length) {
    return null;
  }
  const all: SourcedAuditEventRecord[] = [];
  for (const source of sources) {
    const { records, complete } = await fetchEventsForSource(
      source,
      token,
      apiRoot,
      graphQLEndpoint,
      veritoneAppId
    );
    // NOTHING RATHER THAN SOMETHING, when a source could not be read in full.
    if (!complete) {
      console.warn(
        `[mergedAuditLog] no merged log written: ${source.tdoId}'s history could not be read in full`
      );
      return null;
    }
    all.push(...records);
  }
  /**
   * A LOG MUST HAVE A HISTORY BEHIND IT, and `extraRecords` is not one.
   *
   * This check is deliberately BEFORE the append, and that is the whole
   * finding: with it after, a gather that returned nothing from every source
   * still produced a CSV — one locally-authored row reading "Built merged
   * document (v1)", shipped inside the release, looking like a complete chain
   * of custody for a release with no history. Reachable whenever nothing was
   * ever emitted, because `emitTdoAuditEvent` returns without emitting when no
   * `eventSchemaId` is configured and swallows every rejection, so the caller
   * cannot tell. Before the project source existed, that environment produced
   * no log asset at all; this restores that.
   *
   * It does NOT drop the row in the case the hand-over exists for: a build has
   * members, members have export events, so a refresh-delayed project source
   * still leaves this list non-empty.
   */
  if (!all.length) {
    return null;
  }
  for (const extra of extraRecords) {
    if (!alreadyGathered(all, extra)) {
      all.push(extra);
    }
  }
  // MuPDF license-meter events are Veritone's record, not the customer's chain
  // of custody — kept out here exactly as the per-file log keeps them out.
  return formatMergedAuditEventsCsv(withoutLicenseMeterRecords(all));
}

/**
 * Publish the merged log on the project TDO.
 */
export async function serviceSaveMergedAuditLogAsset(
  projectTdoId: TDOId,
  csv: string,
  filename: string,
  combinedAssetId: string,
  version: number,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<{ id: string; signedUri: string | null } | null> {
  const query = `
    mutation saveMergedAuditLog($id: ID!, $name: String!, $details: JSONData!) {
      createAsset(input: {
        containerId: $id,
        type: "${MERGED_AUDIT_LOG_ASSET_TYPE}",
        contentType: "text/csv",
        name: $name,
        details: $details
      }) {
        id
        name
        signedUri
      }
    }
  `;
  const formData = new FormData();
  formData.append('query', query);
  formData.append(
    'variables',
    JSON.stringify({
      id: projectTdoId,
      name: filename,
      details: { combinedAssetId, version, merged: true },
    })
  );
  formData.append('filename', filename);
  formData.append('file', new Blob([csv], { type: 'text/csv' }), filename);

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (veritoneAppId) {
    headers['x-veritone-application'] = veritoneAppId;
  }
  try {
    const res = await fetch(`${apiRoot}/${graphQLEndpoint}`, {
      method: 'POST',
      body: formData,
      headers,
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const json = (await res.json()) as {
      data?: { createAsset?: { id: string; signedUri: string | null } };
      errors?: Array<{ message?: string }>;
    };
    if (json.errors?.length) {
      throw new Error(json.errors[0]?.message ?? 'unknown error');
    }
    const created = json.data?.createAsset;
    return created ? { id: created.id, signedUri: created.signedUri } : null;
  } catch (error) {
    console.warn('[mergedAuditLog] save failed:', error);
    return null;
  }
}

/** The project TDO's merged-log asset ids, newest first, for rotation. */
export interface MergedAuditLogAssetRecord {
  readonly id: string;
  readonly details?: { readonly combinedAssetId?: string } | null;
}

export async function serviceListMergedAuditLogAssets(
  projectTdoId: TDOId,
  token: string,
  apiRoot: string,
  graphQLEndpoint: string,
  veritoneAppId: string
): Promise<MergedAuditLogAssetRecord[]> {
  // `details` COMES BACK NOW, because a caller deleting one merged document
  // has to tell this log from the log of another build on the same project.
  // The per-file path has always selected it and filters on
  // `details.redactedMediaAssetId`; this one listed bare ids, so the only
  // delete it could express was "all of them".
  const query = `
    query listMergedAuditLogs($id: ID!) {
      temporalDataObject(id: $id) {
        assets(assetType: "${MERGED_AUDIT_LOG_ASSET_TYPE}", orderBy: createdDateTime, orderDirection: desc, limit: 100) {
          records { id details }
        }
      }
    }
  `;
  try {
    const result = await fetchGraphQLApi<{
      temporalDataObject?: {
        assets?: { records?: MergedAuditLogAssetRecord[] } | null;
      } | null;
    }>({
      endpoint: `${apiRoot}/${graphQLEndpoint}`,
      query,
      variables: { id: projectTdoId },
      token,
      veritoneAppId,
    });
    return result?.data?.temporalDataObject?.assets?.records ?? [];
  } catch (error) {
    console.warn('[mergedAuditLog] rotation listing failed:', error);
    return [];
  }
}
